import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { ApiService, SearchFilters, NearbySearchFilters } from '../services/api';
// Now using real API instead of Mock API
// import { MockApiService } from '../services/mockApi';

// Query Keys
export const QueryKeys = {
  COMPANIES: 'companies',
  COMPANY: 'company',
  SEARCH: 'search',
  NEARBY: 'nearby',
  PROVINCES: 'provinces',
  HEALTH: 'health',
  OWNERSHIPS: 'ownerships',
  COMPANY_OWNERSHIP: 'company_ownership',
} as const;

// Cache configuration
const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const GC_TIME = 10 * 60 * 1000; // 10 minutes (gcTime replaces cacheTime in React Query v5)

// Hook for fetching companies with pagination
export function useCompanies(page = 1, perPage = 20) {
  return useQuery({
    queryKey: [QueryKeys.COMPANIES, page, perPage],
    queryFn: () => ApiService.getCompanies(page, perPage),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    retry: 2,
    retryDelay: 1000,
  });
}

// Hook for infinite scroll companies
export function useInfiniteCompanies(perPage = 20) {
  return useInfiniteQuery({
    queryKey: [QueryKeys.COMPANIES, 'infinite', perPage],
    queryFn: ({ pageParam }) => ApiService.getCompanies(pageParam as number, perPage),
    initialPageParam: 1,
    getNextPageParam: (lastPage: any) => {
      if (lastPage.has_next && lastPage.page) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    retry: 2,
  });
}

// Hook for searching companies
export function useSearchCompanies(query: string, filters: SearchFilters = {}, page: number = 1) {
  // Include page in filters for API call
  const searchFilters = { ...filters, page }

  return useQuery({
    queryKey: [QueryKeys.SEARCH, query, searchFilters],
    queryFn: () => ApiService.searchCompanies(query, searchFilters),
    enabled: Boolean(query.trim()) || Object.keys(filters).length > 0,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    retry: 2,
    retryDelay: 1000,
  });
}

// Hook for nearby companies (GPS-based search)
export function useNearbyCompanies(filters: NearbySearchFilters | null) {
  return useQuery({
    queryKey: [QueryKeys.NEARBY, filters],
    queryFn: () => {
      if (!filters) {
        throw new Error('GPS location required');
      }
      return ApiService.getNearbyCompanies(filters);
    },
    enabled: Boolean(filters?.lat && filters?.lng),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    retry: 2,
    retryDelay: 1000,
  });
}

// Hook for fetching single company
export function useCompany(slug: string) {
  return useQuery({
    queryKey: [QueryKeys.COMPANY, slug],
    queryFn: () => ApiService.getCompanyBySlug(slug),
    enabled: Boolean(slug),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    retry: 2,
    retryDelay: 1000,
  });
}

// Hook for fetching provinces
export function useProvinces() {
  return useQuery({
    queryKey: [QueryKeys.PROVINCES],
    queryFn: () => ApiService.getProvinces(),
    staleTime: 60 * 60 * 1000, // 1 hour - provinces don't change often
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: 3,
  });
}

// Hook for health check
export function useHealthCheck() {
  return useQuery({
    queryKey: [QueryKeys.HEALTH],
    queryFn: () => ApiService.healthCheck(),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 2 * 60 * 1000, // 2 minutes
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

// Custom hook for featured companies
export function useFeaturedCompanies(limit = 4) {
  return useQuery({
    queryKey: [QueryKeys.COMPANIES, 'featured', limit],
    queryFn: async () => {
      const response = await ApiService.searchCompanies('', {
        featured: true,
        per_page: limit,
      });
      return response.companies || [];
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    retry: 2,
  });
}

// Custom hook for popular companies (high view count)
export function usePopularCompanies(limit = 6) {
  return useQuery({
    queryKey: [QueryKeys.COMPANIES, 'popular', limit],
    queryFn: async () => {
      const response = await ApiService.getCompanies(1, limit);
      // API should return companies sorted by view count by default
      return response.companies || [];
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    retry: 2,
  });
}

// Removed old useServiceTypes - now using dedicated hook from @/hooks/useServiceTypes
// The new hook fetches from API endpoint /api/service-types/ instead of deriving from companies data

// Hook for fetching user's ownerships
export function useOwnerships(page = 1, perPage = 20) {
  return useQuery({
    queryKey: [QueryKeys.OWNERSHIPS, page, perPage],
    queryFn: () => ApiService.getOwnerships(page, perPage),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    retry: 2,
    retryDelay: 1000,
  });
}

// Hook for checking company ownership status with detailed information
// CRITICAL FIX: Added isAuthenticated parameter to prevent GUEST users from triggering 401 errors
// This fixes the bug where GUEST users get redirected to /login when viewing company detail pages
export function useCompanyOwnership(companyId: string | undefined, isAuthenticated: boolean = false) {
  return useQuery({
    queryKey: [QueryKeys.COMPANY_OWNERSHIP, companyId],
    queryFn: () => companyId ? ApiService.checkCompanyOwnership(companyId) : Promise.resolve({
      status: 'none' as const,
      canClaim: true,
      message: 'คุณสามารถ claim บริษัทนี้ได้'
    }),
    // CRITICAL: Only enable query if user is authenticated AND companyId exists
    // This prevents 401 errors for GUEST users trying to view company details
    enabled: Boolean(companyId) && isAuthenticated,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    retry: 1,
  });
}

export default {
  useCompanies,
  useInfiniteCompanies,
  useSearchCompanies,
  useNearbyCompanies,
  useCompany,
  useProvinces,
  useHealthCheck,
  useFeaturedCompanies,
  usePopularCompanies,
  // useServiceTypes removed - use from @/hooks/useServiceTypes instead
  useOwnerships,
  useCompanyOwnership,
};